import { Injectable, inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { endpoints } from '../../environments/endpoints';
import { ApiErrorResponse, LaravelDefaultResponse } from '../Interfaces/response.interface';
import { CompanyData, OnboarderData, Onboarding } from '../Models/onboarding.model';
import { ApiService } from './api.service';

@Injectable({
  providedIn: 'root',
})
export class OnboardingService {
  private readonly _api = inject(ApiService);

  get(options: { skipLoading?: boolean } = {}): Observable<Onboarding | null> {
    return this._api.get<LaravelDefaultResponse<Onboarding>>(endpoints.onboarding.get, options).pipe(
      map((response) => new Onboarding(response.data)),
      catchError(this.handleError)
    );
  }

  saveCompanyAndOnboarderData(data: {
    company_data: CompanyData;
    onboarder_data: OnboarderData;
  }): Observable<Onboarding> {
    return this._api.put<LaravelDefaultResponse<Onboarding>>(endpoints.onboarding.update, data).pipe(
      map((response) => new Onboarding(response.data)),
      catchError(this.handleError)
    );
  }

  getAdminConsentUrl(): Observable<LaravelDefaultResponse<string>> {
    return this._api
      .get<LaravelDefaultResponse<string>>(endpoints.onboarding.adminConsentRedirect)
      .pipe(catchError(this.handleError));
  }

  complete(
    preferences: { preferred_lang: string; preferred_theme: string },
    options: { skipLoading?: boolean } = {}
  ): Observable<void> {
    return this._api
      .post<LaravelDefaultResponse<null>>(endpoints.onboarding.complete, preferences, options)
      .pipe(
        map(() => void 0),
        catchError(this.handleError)
      );
  }

  private handleError(error: HttpErrorResponse): Observable<never> {
    let errorMessage = 'An unexpected error occurred';

    if (error.error instanceof ErrorEvent) {
      errorMessage = error.error.message;
    } else if (error.error && typeof error.error === 'object') {
      const apiError = error.error as ApiErrorResponse;
      errorMessage = apiError.message || `Server error: ${error.status}`;
    } else if (typeof error.error === 'string') {
      errorMessage = error.error;
    } else {
      errorMessage = error.message || `Server error: ${error.status}`;
    }

    console.error('Onboarding API error:', errorMessage, error);
    return throwError(() => new Error(errorMessage));
  }
}
